import { useEffect, useState } from "react";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import { AppDialog } from "../../components/AppDialog";
import { AppDialogHeader } from "../../components/AppDialogHeader";
import { appState } from "../../../state";
import { getDirectory } from "../../../mentions";
import { inviteGameApi } from "../../../gameApi";
import type { GameDecksResponse, GameMode } from "../../../types";

const MIXED_EMOJI = "\u{1F3B4}";

// Game Home / lobby: replaces the old single-step mode dropdown with a
// deck-picker grid, an inline intensity selector for Spicy/Mixed, and a
// clear "Invite Partner" action - one screen, progressive disclosure,
// rather than a multi-step wizard (kept lightweight per the design brief).
// Recipient auto-resolve is unchanged from v1 (still a 2-user app, no
// picker needed in practice) - same pattern as SendGiftDialog.tsx/
// TogetherInviteDialog.tsx.
export function GameHomeDialog({
  open,
  onClose,
  decksMeta,
}: {
  open: boolean;
  onClose: () => void;
  decksMeta: GameDecksResponse | null;
}) {
  const recipients = getDirectory().filter((u) => u.email !== appState.currentUserEmail);
  const needsRecipientPicker = recipients.length > 1;
  const soleRecipient = recipients.length === 1 ? recipients[0] : null;

  const [selectedMode, setSelectedMode] = useState<GameMode | null>(null);
  const [intensity, setIntensity] = useState(2);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setSelectedMode(null);
    setIntensity(2);
    setError("");
    if (soleRecipient) setRecipientEmail(soleRecipient.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function handleClose(): void {
    if (sending) return;
    onClose();
  }

  const usesIntensity = selectedMode === "spicy" || selectedMode === "mixed";
  const intensityCount = decksMeta?.spicyIntensityCounts?.[intensity];
  const mixedCountAtIntensity =
    selectedMode === "mixed" && decksMeta
      ? (decksMeta.decks.find((d) => d.id === "questions")?.count ?? 0) +
        (decksMeta.decks.find((d) => d.id === "truth_or_dare")?.count ?? 0) +
        (intensityCount ?? 0)
      : null;

  async function handleInvite(): Promise<void> {
    setError("");
    if (!selectedMode) {
      setError("Choose a deck to play.");
      return;
    }
    if (!recipientEmail) {
      setError("Choose who to invite.");
      return;
    }

    setSending(true);
    const result = await inviteGameApi({
      recipientEmail,
      mode: selectedMode,
      intensity: usesIntensity ? intensity : undefined,
    });
    setSending(false);

    if (!result.ok) {
      setError(result.error || "Failed to start game.");
      return;
    }
    onClose();
  }

  return (
    <AppDialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <AppDialogHeader icon="bi-controller" title="Play Together" onBack={handleClose} disabled={sending} />
      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{ display: "grid", gap: 1.5, mb: 2 }}>
          {(decksMeta?.decks || []).map((deck) => (
            <DeckCard
              key={deck.id}
              emoji={deck.emoji}
              label={deck.label}
              description={deck.description}
              count={deck.count}
              hasIntensity={deck.id === "spicy"}
              selected={selectedMode === deck.id}
              onClick={() => setSelectedMode(deck.id as GameMode)}
            />
          ))}
          {decksMeta && (
            <DeckCard
              emoji={MIXED_EMOJI}
              label="Mix It Up"
              description="Random cards from multiple decks"
              count={decksMeta.mixedCount}
              hasIntensity
              selected={selectedMode === "mixed"}
              onClick={() => setSelectedMode("mixed")}
            />
          )}
          {!decksMeta && (
            <Box className="text-center small muted" sx={{ py: 3 }}>
              Loading decks…
            </Box>
          )}
        </Box>

        {usesIntensity && decksMeta && (
          <Box sx={{ mb: 2 }}>
            <Box className="small fw-semibold mb-1">Intensity</Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              {decksMeta.intensityLevels.map((level) => (
                <Button
                  key={level.level}
                  size="small"
                  variant={intensity === level.level ? "contained" : "outlined"}
                  color="primary"
                  onClick={() => setIntensity(level.level)}
                  sx={{ flex: 1, flexDirection: "column", py: 0.75, lineHeight: 1.3 }}
                >
                  <span>{level.emoji}</span>
                  <span style={{ fontSize: "0.7rem" }}>{level.label}</span>
                </Button>
              ))}
            </Box>
            <Box className="small muted mt-1">
              {selectedMode === "mixed" ? mixedCountAtIntensity : intensityCount} cards at this level
            </Box>
          </Box>
        )}

        {needsRecipientPicker && (
          <Box className="small muted mb-2">Inviting {recipientEmail || "…"}</Box>
        )}
        {!recipients.length && (
          <Alert severity="info" sx={{ py: 0.5, mb: 2 }}>
            No one else to invite yet.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ py: 0.5, mb: 2 }}>
            {error}
          </Alert>
        )}

        <Button
          variant="contained"
          color="primary"
          fullWidth
          disabled={sending || !recipients.length || !selectedMode}
          onClick={handleInvite}
          aria-label="Invite partner"
        >
          <i className="bi bi-controller me-2" aria-hidden="true" style={{ marginRight: 8 }} />
          {sending ? "Inviting…" : "Invite Partner"}
        </Button>
      </DialogContent>
    </AppDialog>
  );
}

function DeckCard({
  emoji,
  label,
  description,
  count,
  hasIntensity,
  selected,
  onClick,
}: {
  emoji: string;
  label: string;
  description: string;
  count: number;
  hasIntensity: boolean;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        textAlign: "left",
        width: "100%",
        p: 1.5,
        borderRadius: "var(--radius-sm, 12px)",
        border: "1.5px solid",
        borderColor: selected ? "var(--pink-500)" : "var(--surface-border)",
        background: selected ? "var(--surface-strong)" : "var(--surface-soft)",
        cursor: "pointer",
        transition: "var(--transition)",
      }}
    >
      <span style={{ fontSize: "1.75rem", lineHeight: 1 }}>{emoji}</span>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box className="d-flex align-items-center gap-1">
          <span className="fw-semibold">{label}</span>
          {hasIntensity && <span style={{ fontSize: "0.85rem" }}>{"\u{1F336}\u{FE0F}"}</span>}
        </Box>
        <Box className="small muted">{description}</Box>
        <Box className="small muted">~{count} cards</Box>
      </Box>
      {selected && <i className="bi bi-check-circle-fill text-pink" aria-hidden="true" />}
    </Box>
  );
}
